import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://pis.ap.io.vn/api/v1';

export interface NASConfig {
  id?: string;
  protocol: 'smb' | 'nfs';
  host: string;
  port: number;
  share_name: string;
  username?: string;
  password?: string;
  mount_path: string;
  backup_minio: boolean;
  backup_database: boolean;
  backup_media: boolean;
  enabled: boolean;
  is_mounted?: boolean;
  last_test?: string;
  last_test_success?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface BackupSchedule {
  id?: string;
  frequency: 'daily' | 'weekly' | 'monthly';
  time: string;
  day_of_week?: number;
  day_of_month?: number;
  retention_days: number;
  enabled: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface BackupHistory {
  id: string;
  backup_type: 'manual' | 'scheduled' | 'auto';
  status: 'pending' | 'running' | 'success' | 'failed' | 'cancelled';
  started_at: string;
  completed_at?: string;
  duration_seconds?: number;
  duration_formatted?: string;
  files_backed_up: number;
  total_size_bytes: number;
  size_formatted: string;
  minio_files: number;
  minio_size_bytes: number;
  database_files: number;
  database_size_bytes: number;
  error_message?: string;
  backup_path?: string;
  celery_task_id?: string;
}

export interface BackupStats {
  total_backups: number;
  successful_backups: number;
  failed_backups: number;
  total_size_bytes: number;
  avg_duration_seconds: number;
  latest_success?: BackupHistory;
  latest_failure?: BackupHistory;
}

class BackupService {
  private getAuthHeaders() {
    const token = localStorage.getItem('token');
    return {
      headers: {
        Authorization: `Token ${token}`,
        'Content-Type': 'application/json',
      },
    };
  }

  // NAS Config API
  async getNASConfigs(): Promise<NASConfig[]> {
    const response = await axios.get(`${API_URL}/backup/nas-config/`, this.getAuthHeaders());
    return response.data;
  }

  async getNASConfig(id: string): Promise<NASConfig> {
    const response = await axios.get(`${API_URL}/backup/nas-config/${id}/`, this.getAuthHeaders());
    return response.data;
  }

  async createNASConfig(config: Partial<NASConfig>): Promise<NASConfig> {
    const response = await axios.post(`${API_URL}/backup/nas-config/`, config, this.getAuthHeaders());
    return response.data;
  }

  async updateNASConfig(id: string, config: Partial<NASConfig>): Promise<NASConfig> {
    const response = await axios.put(`${API_URL}/backup/nas-config/${id}/`, config, this.getAuthHeaders());
    return response.data;
  }

  async deleteNASConfig(id: string): Promise<void> {
    await axios.delete(`${API_URL}/backup/nas-config/${id}/`, this.getAuthHeaders());
  }

  async testNASConnection(id: string): Promise<{ success: boolean; message: string; tested_at: string }> {
    const response = await axios.post(`${API_URL}/backup/nas-config/${id}/test/`, {}, this.getAuthHeaders());
    return response.data;
  }

  async mountNAS(id: string): Promise<{ success: boolean; message: string; is_mounted: boolean }> {
    const response = await axios.post(`${API_URL}/backup/nas-config/${id}/mount/`, {}, this.getAuthHeaders());
    return response.data;
  }

  async unmountNAS(id: string): Promise<{ success: boolean; message: string; is_mounted: boolean }> {
    const response = await axios.post(`${API_URL}/backup/nas-config/${id}/unmount/`, {}, this.getAuthHeaders());
    return response.data;
  }

  // Backup Schedule API
  async getSchedules(): Promise<BackupSchedule[]> {
    const response = await axios.get(`${API_URL}/backup/schedule/`, this.getAuthHeaders());
    return response.data;
  }

  async getSchedule(id: string): Promise<BackupSchedule> {
    const response = await axios.get(`${API_URL}/backup/schedule/${id}/`, this.getAuthHeaders());
    return response.data;
  }

  async createSchedule(schedule: Partial<BackupSchedule>): Promise<BackupSchedule> {
    const response = await axios.post(`${API_URL}/backup/schedule/`, schedule, this.getAuthHeaders());
    return response.data;
  }

  async updateSchedule(id: string, schedule: Partial<BackupSchedule>): Promise<BackupSchedule> {
    const response = await axios.put(`${API_URL}/backup/schedule/${id}/`, schedule, this.getAuthHeaders());
    return response.data;
  }

  async deleteSchedule(id: string): Promise<void> {
    await axios.delete(`${API_URL}/backup/schedule/${id}/`, this.getAuthHeaders());
  }

  // Backup History API
  async getHistory(page: number = 1, pageSize: number = 10): Promise<{ results: BackupHistory[]; count: number }> {
    const response = await axios.get(`${API_URL}/backup/history/?page=${page}&page_size=${pageSize}`, this.getAuthHeaders());
    return response.data;
  }

  async getBackup(id: string): Promise<BackupHistory> {
    const response = await axios.get(`${API_URL}/backup/history/${id}/`, this.getAuthHeaders());
    return response.data;
  }

  async triggerBackup(): Promise<{ success: boolean; message: string; backup_id: string; task_id: string }> {
    const response = await axios.post(`${API_URL}/backup/history/trigger/`, {}, this.getAuthHeaders());
    return response.data;
  }

  async getLatestBackup(): Promise<BackupHistory> {
    const response = await axios.get(`${API_URL}/backup/history/latest/`, this.getAuthHeaders());
    return response.data;
  }

  async getStats(): Promise<BackupStats> {
    const response = await axios.get(`${API_URL}/backup/history/stats/`, this.getAuthHeaders());
    return response.data;
  }
}

export const backupService = new BackupService();
